import { useEffect, useState, useRef } from 'react';
import { tasksAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';

const GlobalTaskMonitor = () => {
  const { showSuccess, showError, showWarning } = useNotification();
  const [monitoredTasks, setMonitoredTasks] = useState(new Set());
  const pollingInterval = useRef(null);
  const notifiedTasks = useRef(new Set()); // Track which tasks we've already notified about

  useEffect(() => {
    // Fetch running/pending tasks on mount
    fetchRunningTasks();

    // Poll every 5 seconds
    pollingInterval.current = setInterval(() => {
      if (monitoredTasks.size > 0) {
        checkTasksStatus();
      }
    }, 5000);

    // Also fetch tasks every 10 seconds to catch new ones (reduced from 30s for faster detection)
    const fetchInterval = setInterval(() => {
      fetchRunningTasks();
    }, 10000);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      clearInterval(fetchInterval);
    };
  }, [monitoredTasks.size]);

  const fetchRunningTasks = async () => {
    try {
      // Fetch tasks in every NON-terminal status, incl. the new state machine
      // (dispatching/pausing/paused/slot_wait/cancelling) — else paused/slot_wait
      // tasks are invisible and polled forever with no user-visible resolution.
      const nonTerminal = ['running', 'pending', 'dispatching', 'pausing',
                           'paused', 'slot_wait', 'cancelling'];
      const responses = await Promise.all(nonTerminal.map((st) =>
        tasksAPI.getTasks(1, 100, st, null).catch(() => ({ data: { tasks: [] } }))
      ));

      const allTasks = responses.flatMap((r) => r.data?.tasks || []);

      const taskIds = new Set(allTasks.map(task => task._id));
      
      if (taskIds.size > 0) {
        console.log('GlobalTaskMonitor: Monitoring', taskIds.size, 'tasks');
        setMonitoredTasks(taskIds);
      }
    } catch (err) {
      console.error('GlobalTaskMonitor: Failed to fetch running tasks:', err);
    }
  };

  const checkTasksStatus = async () => {
    const tasksToCheck = Array.from(monitoredTasks);
    
    for (const taskId of tasksToCheck) {
      try {
        const response = await tasksAPI.getTaskStatus(taskId);
        const status = response.data.status;
        const task = response.data;

        // Check if task is completed and we haven't notified about it yet
        if ((status === 'completed' || status === 'failed' || status === 'cancelled') 
            && !notifiedTasks.current.has(taskId)) {
          
          console.log('GlobalTaskMonitor: Task completed:', taskId, 'Status:', status);
          
          // Mark as notified
          notifiedTasks.current.add(taskId);
          
          // Remove from monitoring
          setMonitoredTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
          });

          // Show notification
          showTaskNotification(task, status);
        }
      } catch (err) {
        console.error('GlobalTaskMonitor: Failed to check task status:', taskId, err);
        // Remove task from monitoring if it doesn't exist
        if (err.response?.status === 404) {
          setMonitoredTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
          });
        }
      }
    }
  };

  const showTaskNotification = (task, status) => {
    const toolName = task.tool || 'Unknown tool';
    
    const messages = {
      completed: `Task "${toolName}" completed successfully! 🎉`,
      failed: `Task "${toolName}" failed! ❌`,
      cancelled: `Task "${toolName}" was cancelled. ⚠️`
    };

    const message = messages[status] || `Task "${toolName}" status: ${status}`;
    
    console.log('GlobalTaskMonitor: Showing notification:', message);
    
    switch (status) {
      case 'completed':
        showSuccess(message, 7000);
        break;
      case 'failed':
        showError(message, 10000);
        break;
      case 'cancelled':
        showWarning(message, 7000);
        break;
      default:
        break;
    }
  };

  // This component doesn't render anything
  return null;
};

export default GlobalTaskMonitor;

